//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import * as Css from 'csstype';
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { OverlayIconMediaButton } from '@components/atoms/button/Button';
import { MediaAtomPositionMixins } from '@components/atoms/media/MediaAtomPositionMixins';
import { useIsElementVisible } from '@hooks/useIsElementVisible';
import { useVideo } from '@hooks/useVideo';
import { isDefined } from '@utils/filterPredicates';

export interface IPlayButton {
  playIconWidth: string;
  playButtonBackgroundSize?: string;
  shouldIgnoreOffsetOnPlay?: boolean;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  backgroundColor?: string;
  controlsList?: string;
  coverImageUrl?: string;
  isMediaHovered: boolean;
  objectFit?: Css.Property.ObjectFit;
  overlayContent?: React.ReactNode;
  playButton?: IPlayButton;
  setIsVideoPlaying?: (b: boolean) => void;
  shouldPlayOnHover?: boolean;
  thumbnailOffsetSeconds?: number;
  videoComponent?: React.ReactNode;
  videoUrl: string;
}

/**
 * @param setIsVideoPlaying when defined, setIsVideoPlaying will send video playing info to parent component to allow for styling variations
 * @param shouldPlayOnHover will automatically play the video on hover and will pause when not hovering unless shouldShowPlayButton
 * @param playButton if playButton is defined, the play icon with width of playButton.playIconWidth will be centered over the video and when clicked the video will play and only
 * then will the default html video controls be displayed. The play icon will appear after the video finishes or if there is a new videoUrl. Has an optional
 * playButtonBackgroundSize (default is 32px)
 * @param thumbnailOffsetSeconds will start the video at the selected offset in seconds
 * @param controlsList a string that contains which controls to leave out such as 'nodownload noplaybackrate noremoteplayback'
 *
 */
const VideoLayer = ({
  backgroundColor,
  controlsList,
  coverImageUrl,
  isMediaHovered,
  objectFit = 'contain',
  overlayContent,
  playButton = undefined,
  setIsVideoPlaying,
  shouldPlayOnHover = true,
  thumbnailOffsetSeconds = 0,
  videoComponent,
  videoUrl,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const { isPlaying, setIsPlaying, togglePlayVideo } = useVideo({
    coverImageUrl,
    isMediaHovered,
    ref: videoRef,
    shouldPlayOnHover,
    shouldIgnoreOffsetOnPlay: playButton?.shouldIgnoreOffsetOnPlay,
    shouldStartMuted: true,
    thumbnailOffsetSeconds,
    videoUrl,
  });

  const isVisible = useIsElementVisible(videoRef);

  useEffect(() => {
    if (!isVisible && isPlaying) {
      void togglePlayVideo();
    }
  }, [isVisible]);

  useEffect(() => {
    if (setIsVideoPlaying) {
      isPlaying ? setIsVideoPlaying(false) : setIsVideoPlaying(true);
    }
  }, [isPlaying, setIsVideoPlaying]);

  return (
    <VideoWrapper>
      {isDefined(videoComponent) ? (
        videoComponent
      ) : (
        <>
          <ThumbnailVideo
            ref={videoRef}
            $backgroundColor={backgroundColor}
            $objectFit={objectFit}
            controls={isPlaying && !shouldPlayOnHover}
            controlsList={controlsList}
            muted={true}
            // prevent the video from togglePlayVideo when button isn't rendered
            onClick={(e) => e.preventDefault()}
            onEnded={() => {
              setIsPlaying(false);
              if (videoRef.current) {
                videoRef.current.load(); // Re-render cover image after video ends
                videoRef.current.currentTime = thumbnailOffsetSeconds;
              }
            }}
            onPause={() => {
              if (!videoRef.current?.seeking) {
                setIsPlaying(false);
              }
            }}
            onPlay={() => setIsPlaying(true)}
            poster={coverImageUrl}
            src={videoUrl}
          />

          {!isPlaying && isDefined(playButton) && (
            <ControlButton
              $buttonBackgroundSize={playButton.playButtonBackgroundSize}
              $iconWidth={playButton.playIconWidth}
              aria-label={'Play video'}
              iconName={'play'}
              onClick={(event: React.MouseEvent) => {
                // prevent the click functionality from bubbling up
                event.stopPropagation();
                void togglePlayVideo();
              }}
            />
          )}

          {isDefined(overlayContent) && (
            <Overlay $isPlaying={isPlaying}>{overlayContent}</Overlay>
          )}
        </>
      )}
    </VideoWrapper>
  );
};

const VideoWrapper = styled.div`
  ${MediaAtomPositionMixins.Cover};
`;

const ThumbnailVideo = styled.video<{
  $backgroundColor?: string;
  $objectFit: Css.Property.ObjectFit;
}>(
  ({ $backgroundColor, $objectFit, theme }) => `
    background-color: ${$backgroundColor ?? theme.colors.gray0};
    width: 100%;
    height: 100%;
    object-fit: ${$objectFit};
    `,
);

const ControlButton = styled(OverlayIconMediaButton)<{
  $iconWidth: string;
  $buttonBackgroundSize?: string;
}>(
  ({ $buttonBackgroundSize, $iconWidth, theme }) => css`
    ${MediaAtomPositionMixins.Middle}
    ${$buttonBackgroundSize &&
    `
      height: ${$buttonBackgroundSize};
      width: ${$buttonBackgroundSize};
      `}
      
      && svg {
      width: ${$iconWidth};
    }

    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    box-shadow: none;
    z-index: ${theme.zindex.navbar};
  `,
);

const Overlay = styled.div<{ $isPlaying: boolean }>(
  ({ $isPlaying }) => `
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  ${$isPlaying && 'pointer-events: none;'}
  `,
);

export default VideoLayer;
